import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, CheckCircle, Loader2, Lock, Clock, ExternalLink, Video, X, Globe } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { useUserProfile } from '@/features/profile/hooks/useUserProfile';
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig';
import { useRollReviewSettings } from '@/features/roll-review/hooks/useRollReviewSettings';
import { PayoutCountryStep } from '@/features/wallet/components/PayoutCountryStep';
import { useFormattedPayoutMinimum } from '@/features/wallet/hooks/usePayoutMinimum';
import { useRecordAgreementAcceptance } from '@/features/wallet/hooks/useRecordAgreementAcceptance';
import { useToast } from '@/shared/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';
import { interpolateNodes } from '@/lib/interpolateNodes';
import { isPayoutCountrySupported } from '@/lib/payoutCountries';
import {
  calculatePriceBreakdown,
  decimalToCents,
  formatCurrency,
  PLATFORM_COMMISSION_PERCENT,
} from '@/lib/pricing';
import { SOSSIETY_RED } from '@/styles/brand';
import { emit } from '@/integrations/analytics/emit';
import { PAYOUT_SETUP_CTA_SHOWN } from '@/integrations/analytics/events';

// ─── Design tokens ────────────────────────────────────────────────────────────

const C = {
  bg:       '#160C0C',
  surface:  '#281B1B',
  surface2: '#1F1313',
  border:   '#FFFFFF1A',
  text:     '#FFFFFF',
  muted:    '#8D8D8D',
  accent:   SOSSIETY_RED,
  green:    '#22C55E',
  stripe:   '#635BFF',
};

/**
 * Currency the price captured by this wizard is stored in.
 *
 * `handleFinish` already read a `CURRENCY` identifier that was declared nowhere
 * in the module — a free variable, and a `ReferenceError` the moment that
 * handler ran. It has not been reported because the step it belongs to is
 * currently unreachable (see `EarningsPage`), so this is a latent crash rather
 * than a live one; either way the reference has to resolve. 'USD' matches the
 * "PRICE PER REVIEW (USD)" label this screen shows and the currency the wallet
 * wizard writes.
 */
const CURRENCY = 'USD';

/** Price used by the earnings example on the welcome screen. */
const EXAMPLE_PRICE_CENTS = 1500;

type Screen = 'welcome' | 'contract' | 'stripe' | 'service';

const STEP_MAP: Record<Screen, number | null> = {
  welcome: null,
  contract: 1,
  stripe: 2,
  service: 3,
};

// ─── Small helpers ─────────────────────────────────────────────────────────────

/**
 * What the instructor keeps on a review at `priceDollars`, formatted.
 *
 * Delegates to `calculatePriceBreakdown` — the same function the payment intent
 * uses — rather than recomputing `price * (1 - commission)` locally. A second
 * formula is a second thing to forget when the rate moves, and forgetting it is
 * exactly how this screen came to advertise 10% while charging 20%.
 */
function netDisplay(priceDollars: number): string {
  const breakdown = calculatePriceBreakdown(decimalToCents(priceDollars));
  return formatCurrency(breakdown.netToInstructorCents, CURRENCY);
}

// ─── StepBadge — numbered red circle ─────────────────────────────────────────

function StepBadge({ n }: { n: number }) {
  return (
    <div
      className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-bold mt-0.5"
      style={{ backgroundColor: C.accent, color: '#FFFFFF' }}
    >
      {n}
    </div>
  );
}

// ─── Wizard chrome (header + progress bar) ────────────────────────────────────

function WizardHeader({
  step,
  onBack,
}: {
  step: number;
  onBack: () => void;
}) {
  return (
    <div>
      {/* Top bar */}
      <div className="flex items-center justify-between px-5 pt-5 pb-4">
        <button onClick={onBack} className="w-8 h-8 flex items-center justify-center" aria-label="Back">
          <ChevronLeft className="w-5 h-5" style={{ color: C.text }} />
        </button>
        <span className="text-sm font-semibold" style={{ color: C.text }}>Wallet setup</span>
        <span className="text-xs" style={{ color: C.muted }}>Step {step} of 3</span>
      </div>
      {/* Progress bar */}
      <div className="flex gap-1.5 px-5 pb-6">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className="flex-1 h-1 rounded-full transition-colors"
            style={{ backgroundColor: step >= s ? C.accent : '#FFFFFF22' }}
          />
        ))}
      </div>
    </div>
  );
}

// ─── A — Welcome / Pre-onboarding ─────────────────────────────────────────────

function WelcomeScreen({ onStart }: { onStart: () => void }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const minimum = useFormattedPayoutMinimum();
  const breakdown = calculatePriceBreakdown(EXAMPLE_PRICE_CENTS);

  // Connect-payouts CTA impression. The stripe_accounts row is created on the
  // "Continue on Stripe" click in the later step, so this impression is the
  // PostHog-owned gap between "instructor opened the wallet setup" and the
  // first action that produces a database row.
  useEffect(() => {
    emit(PAYOUT_SETUP_CTA_SHOWN);
  }, []);

  return (
    <div className="flex flex-col h-screen" style={{ backgroundColor: C.bg }}>
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 pb-4">
        {/* Close */}
        <div className="flex justify-end pt-5 pb-2">
          <button
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center rounded-full"
            style={{ backgroundColor: '#FFFFFF14' }}
          >
            <X className="w-4 h-4" style={{ color: C.muted }} />
          </button>
        </div>

        {/* Icon */}
        <div className="flex justify-center my-6">
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${C.accent}22` }}
          >
            <Video className="w-7 h-7" style={{ color: C.accent }} />
          </div>
        </div>

        {/* Headline */}
        <h1
          data-testid="wallet-welcome-title"
          className="text-2xl font-bold text-center mb-3"
          style={{ color: C.text }}
        >
          {t('wallet.setup.welcome.hero_title')}
        </h1>
        <p className="text-sm text-center mb-8" style={{ color: C.muted }}>
          {t('wallet.setup.welcome.hero_sub')}
        </p>

        {/* Example card */}
        <div
          className="rounded-2xl p-5 mb-8"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
        >
          <p className="text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: C.muted }}>
            {t('wallet.setup.welcome.example_label', {
              price: formatCurrency(breakdown.priceCents, CURRENCY),
            })}
          </p>
          {/*
            Every figure here comes from `calculatePriceBreakdown`, the same
            function the payment intent uses. The card used to hard-code
            "You receive $13.50" on a $15 review — a 10% split, from before
            SOS-319 raised the commission to 20% — so the screen quoted a net
            the engine never paid.
          */}
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span style={{ color: C.muted }}>{t('wallet.setup.welcome.your_price')}</span>
              <span style={{ color: C.text }}>{formatCurrency(breakdown.priceCents, CURRENCY)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span style={{ color: C.muted }}>
                {t('wallet.setup.welcome.commission', { percent: PLATFORM_COMMISSION_PERCENT })}
              </span>
              <span style={{ color: C.text }}>
                −{formatCurrency(breakdown.commissionCents, CURRENCY)}
              </span>
            </div>
            <div
              className="flex justify-between text-sm font-semibold pt-2"
              style={{ borderTop: `1px solid ${C.border}` }}
            >
              <span style={{ color: C.text }}>{t('wallet.setup.welcome.you_receive')}</span>
              <span style={{ color: C.green }} data-testid="welcome-net-amount">
                {formatCurrency(breakdown.netToInstructorCents, CURRENCY)}
              </span>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="space-y-5">
          {/*
            Copy comes from the `wallet.setup.welcome.*` keys rather than from
            literals: step 2 claimed "48h to deliver", which is the *acceptance*
            window — delivery is 72h (`DELIVERY_WINDOW_HOURS`) — and step 3
            promised a Friday payout with no mention of the per-currency
            minimum the release engine enforces. Both are already stated
            correctly, and translated, in those keys.
          */}
          {[
            {
              n: 1,
              title: t('wallet.setup.welcome.step1_title'),
              desc: t('wallet.setup.welcome.step1_sub'),
            },
            {
              n: 2,
              title: t('wallet.setup.welcome.step2_title'),
              desc: t('wallet.setup.welcome.step2_sub'),
            },
            {
              n: 3,
              title: t('wallet.setup.welcome.step3_title'),
              desc: t('wallet.setup.welcome.step3_sub', { minimum }),
            },
          ].map(({ n, title, desc }) => (
            <div key={n} className="flex gap-4">
              <StepBadge n={n} />
              <div>
                <p className="text-sm font-semibold" style={{ color: C.text }}>{title}</p>
                <p className="text-xs mt-0.5" style={{ color: C.muted }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fixed CTA */}
      <div className="px-5 py-5 pb-safe" style={{ backgroundColor: C.bg }}>
        <button
          onClick={onStart}
          className="w-full py-4 rounded-full font-semibold text-sm text-white"
          style={{ backgroundColor: C.accent }}
        >
          {t('wallet.setup.welcome.cta')}
        </button>
      </div>
    </div>
  );
}

// ─── B — Step 1: Contractor agreement ─────────────────────────────────────────

function ContractScreen({
  onBack,
  onContinue,
}: {
  onBack: () => void;
  onContinue: () => void;
}) {
  const { t } = useI18n();
  const minimum = useFormattedPayoutMinimum();
  const [accepted, setAccepted] = useState(false);

  /**
   * Records the acceptance, and only then leaves the step.
   *
   * This is the screen instructors actually reach — `/settings/instructor-tools/
   * earnings`, linked from Settings, the wallet and the Roll Review pricing tab.
   * Until now its checkbox lived in local state and was discarded on
   * navigation, so no acceptance row was ever written from the live flow. That
   * matters beyond the audit trail: `requireAgreementAccepted` is fail-closed
   * on `stripe-connect-onboard`, `roll-review-accept` and
   * `stripe-create-payment-intent`, so an instructor who passed through here
   * was refused at the Stripe step with a generic error.
   *
   * The write itself lives in `useRecordAgreementAcceptance`, shared with
   * `WalletSetupPage`'s equivalent step: this is the one write the agreement
   * gate is fail-closed on, and two copies of it are two things to forget.
   * `onSuccess` is the only path that advances the step.
   */
  const { recordAcceptance, isRecordingAcceptance, acceptanceError } =
    useRecordAgreementAcceptance({
      acceptanceAction: 'earnings_setup_checkbox',
      onSuccess: onContinue,
    });

  const terms = [
    {
      label: t('wallet.setup.ica.relationship_label'),
      value: t('wallet.setup.ica.relationship_value'),
    },
    {
      label: t('wallet.setup.ica.commission_label'),
      value: t('wallet.setup.ica.commission_value', { percent: PLATFORM_COMMISSION_PERCENT }),
    },
    {
      label: t('wallet.setup.ica.delivery_label'),
      value: t('wallet.setup.ica.delivery_value'),
    },
    // "Weekly via Stripe" said nothing about the threshold that decides whether
    // a given Friday pays out at all. The minimum comes from the same map the
    // release engine reads.
    {
      label: t('wallet.setup.ica.payouts_label'),
      value: t('wallet.setup.ica.payouts_value', { minimum }),
    },
  ];

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: C.bg }}>
      <WizardHeader step={1} onBack={onBack} />

      <div className="flex-1 px-5 overflow-y-auto">
        <h1 className="text-2xl font-bold mb-2" style={{ color: C.text }}>
          {t('wallet.setup.ica.heading')}
        </h1>
        <p className="text-sm mb-6" style={{ color: C.muted }}>
          {t('wallet.setup.ica.intro')}
        </p>

        {/* Summary table */}
        <div
          className="rounded-2xl overflow-hidden mb-6"
          style={{ border: `1px solid ${C.border}` }}
        >
          {terms.map(({ label, value }, i) => (
            <div
              key={label}
              className="flex items-center justify-between px-5 py-4"
              style={{
                backgroundColor: i % 2 === 0 ? C.surface : C.surface2,
              }}
            >
              <span className="text-sm" style={{ color: C.muted }}>{label}</span>
              <span className="text-sm font-medium text-right" style={{ color: C.text }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Checkbox */}
        <div
          className="flex items-start gap-3 p-4 rounded-2xl mb-6"
          style={{
            border: `1px solid ${accepted ? C.accent : C.border}`,
            backgroundColor: accepted ? `${C.accent}14` : 'transparent',
          }}
        >
          <Checkbox
            id="accept-ica"
            checked={accepted}
            onCheckedChange={(v) => setAccepted(v as boolean)}
            className="mt-0.5"
          />
          {/*
            Both documents are real routes — `/independent-contractor-agreement`
            and `/terms-of-use` — and were rendered as plain underlined spans,
            so the instructor was asked to accept a contract they could not
            open. `stopPropagation` keeps a tap on a link from also toggling the
            checkbox through the surrounding label.

            `py-3` is the touch target, not decoration: vertical padding on an
            inline element grows the hit box without growing the line box, so
            the sentence still wraps normally and nothing moves. +12px above and
            below a ~20px line box clears the 44px floor (DOC-03 AC-09) — the
            same treatment `WalletSetupPage` gives the identical links.
          */}
          <label htmlFor="accept-ica" className="text-sm cursor-pointer" style={{ color: C.text }}>
            {interpolateNodes(t('wallet.setup.ica.agree_sentence'), {
              agreement: (
                <a
                  href="/independent-contractor-agreement"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sossiety-red hover:underline py-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('wallet.setup.ica.agreement_link')}
                </a>
              ),
              terms: (
                <a
                  href="/terms-of-use"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sossiety-red hover:underline py-3"
                  onClick={(e) => e.stopPropagation()}
                >
                  {t('wallet.setup.ica.terms_link')}
                </a>
              ),
            })}
          </label>
        </div>

        {acceptanceError && (
          <p role="alert" className="text-xs text-center mb-6" style={{ color: '#FF6B6B' }}>
            {t('wallet.setup.ica.record_error')}
          </p>
        )}
      </div>

      <div className="px-5 pb-8 pt-4">
        <button
          onClick={() => recordAcceptance()}
          disabled={!accepted || isRecordingAcceptance}
          className="w-full py-4 rounded-full font-semibold text-sm text-white transition-opacity"
          style={{
            backgroundColor: C.accent,
            opacity: accepted && !isRecordingAcceptance ? 1 : 0.4,
          }}
        >
          {isRecordingAcceptance
            ? t('wallet.setup.ica.recording')
            : t('wallet.setup.ica.continue')}
        </button>
      </div>
    </div>
  );
}

// ─── C — Step 2: Stripe KYC handoff ───────────────────────────────────────────

function StripeScreen({
  onBack,
  onContinue,
  isLoading,
}: {
  onBack: () => void;
  onContinue: () => void;
  isLoading: boolean;
}) {
  const { t } = useI18n();

  const bullets = [
    {
      icon: CheckCircle,
      text: t('wallet.setup.stripe.tax_title'),
      sub:  t('wallet.setup.stripe.tax_sub'),
    },
    {
      icon: Lock,
      text: t('wallet.setup.stripe.bank_link_title'),
      sub:  t('wallet.setup.stripe.bank_link_sub'),
    },
    {
      icon: Clock,
      text: t('wallet.setup.stripe.duration_title'),
      sub:  t('wallet.setup.stripe.duration_sub'),
    },
  ];

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: C.bg }}>
      <WizardHeader step={2} onBack={onBack} />

      <div className="flex-1 px-5">
        {/* Stripe logo */}
        <div className="flex justify-center mb-6">
          <div
            className="w-14 h-14 rounded-xl flex items-center justify-center text-white text-2xl font-bold"
            style={{ backgroundColor: C.stripe }}
          >
            S
          </div>
        </div>

        <h1 className="text-2xl font-bold text-center mb-3" style={{ color: C.text }}>
          Continue on Stripe
        </h1>
        <p className="text-sm text-center mb-8" style={{ color: C.muted }}>
          Stripe handles your tax info, identity and bank details with bank-grade security.
        </p>

        {/* Bullets */}
        <div
          className="rounded-2xl overflow-hidden mb-6"
          style={{ border: `1px solid ${C.border}`, backgroundColor: C.surface }}
        >
          {bullets.map(({ icon: Icon, text, sub }, i) => (
            <div
              key={text}
              className="flex items-start gap-4 px-5 py-4"
              style={{ borderTop: i > 0 ? `1px solid ${C.border}` : 'none' }}
            >
              <div
                className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
                style={{ backgroundColor: '#22C55E22' }}
              >
                <Icon className="w-4 h-4" style={{ color: C.green }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: C.text }}>{text}</p>
                <p className="text-xs mt-0.5" style={{ color: C.muted }}>{sub}</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-center mb-8" style={{ color: C.muted }}>
          If Stripe needs extra documents, you'll receive an email and can return to retry.
        </p>
      </div>

      <div className="px-5 pb-8 pt-4">
        <button
          onClick={onContinue}
          disabled={isLoading}
          className="w-full py-4 rounded-full font-semibold text-sm text-white flex items-center justify-center gap-2"
          style={{ backgroundColor: C.stripe }}
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Continue on Stripe
              <ExternalLink className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── D — Step 3: Service config ───────────────────────────────────────────────

function ServiceScreen({
  onBack,
  onFinish,
  isSaving,
}: {
  onBack: () => void;
  onFinish: (price: number, acceptRequests: boolean) => void;
  isSaving: boolean;
}) {
  const { t } = useI18n();
  const { config } = useAgendaConfig();
  const { acceptRollReviews } = useRollReviewSettings();

  const [priceStr, setPriceStr] = useState(
    config?.class_value ? String(Math.round(config.class_value)) : '15'
  );
  const [acceptRequests, setAcceptRequests] = useState(acceptRollReviews);

  const price    = parseFloat(priceStr) || 0;
  const net      = netDisplay(price);
  const isValid  = price >= 5;

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: C.bg }}>
      <WizardHeader step={3} onBack={onBack} />

      <div className="flex-1 px-5 space-y-6">
        <div>
          <h1 className="text-2xl font-bold mb-1" style={{ color: C.text }}>
            Set up your service
          </h1>
          <p className="text-sm" style={{ color: C.muted }}>
            Change any of this later in Instructor Tools.
          </p>
        </div>

        {/* Price input */}
        <div>
          <p className="text-xs font-semibold tracking-widest mb-3" style={{ color: C.muted }}>
            PRICE PER REVIEW (USD)
          </p>
          <div
            className="flex items-center px-5 py-4 rounded-2xl"
            style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
          >
            <span className="text-3xl font-light mr-2" style={{ color: C.muted }}>$</span>
            <input
              type="number"
              min={5}
              max={999}
              value={priceStr}
              onChange={(e) => setPriceStr(e.target.value)}
              className="flex-1 bg-transparent text-3xl font-semibold outline-none"
              style={{ color: C.text }}
            />
          </div>
          {price > 0 && (
            <p className="text-xs mt-2 text-center" style={{ color: C.muted }}>
              {/*
                `wallet.setup.service.preview` is this exact sentence, in both
                bundles, and the sibling wizard already renders it. Assembling
                it from literals here left the one line of this screen that
                quotes the instructor their own earnings untranslated.
              */}
              {interpolateNodes(t('wallet.setup.service.preview'), {
                net: <span style={{ color: C.green }}>{net}</span>,
                price: formatCurrency(decimalToCents(price), CURRENCY),
              })}
            </p>
          )}
          {price > 0 && price < 5 && (
            <p className="text-xs mt-2 text-center" style={{ color: C.accent }}>
              Minimum price is $5
            </p>
          )}
        </div>

        {/* Turnaround */}
        <div
          className="flex items-center justify-between px-5 py-4 rounded-2xl"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
        >
          <div>
            <p className="text-sm font-medium" style={{ color: C.text }}>
              {t('wallet.setup.service.turnaround_title')}
            </p>
            <p className="text-xs mt-0.5" style={{ color: C.muted }}>
              {t('wallet.setup.service.turnaround_sub')}
            </p>
          </div>
          <span className="text-sm font-semibold" style={{ color: C.text }}>
            {t('wallet.setup.service.turnaround_value')}
          </span>
        </div>

        {/* Accept toggle */}
        <div
          className="flex items-center justify-between px-5 py-4 rounded-2xl"
          style={{ backgroundColor: C.surface, border: `1px solid ${C.border}` }}
        >
          <div>
            <p className="text-sm font-medium" style={{ color: C.text }}>Accept new requests</p>
            <p className="text-xs mt-0.5" style={{ color: C.muted }}>Pause anytime without leaving sOSSiety.</p>
          </div>
          <Switch
            checked={acceptRequests}
            onCheckedChange={setAcceptRequests}
          />
        </div>
      </div>

      <div className="px-5 pb-8 pt-4">
        <button
          onClick={() => onFinish(price, acceptRequests)}
          disabled={!isValid || isSaving}
          className="w-full py-4 rounded-full font-semibold text-sm text-white flex items-center justify-center gap-2"
          style={{ backgroundColor: C.accent, opacity: isValid && !isSaving ? 1 : 0.4 }}
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Finish setup'}
        </button>
      </div>
    </div>
  );
}

// ─── Pending verification state ───────────────────────────────────────────────

function PendingScreen({
  onRetry,
  onRefresh,
  isRetrying,
  isRefreshing,
}: {
  onRetry: () => void;
  onRefresh: () => void;
  isRetrying: boolean;
  isRefreshing: boolean;
}) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-5 pb-safe" style={{ backgroundColor: C.bg }}>
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: '#EAB30822' }}
      >
        <Clock className="w-7 h-7" style={{ color: '#EAB308' }} />
      </div>
      <h1 className="text-xl font-bold text-center mb-2" style={{ color: C.text }}>
        Stripe verification in progress
      </h1>
      <p className="text-sm text-center mb-8" style={{ color: C.muted }}>
        Stripe is finishing your verification. This usually takes a few minutes.
        Tap "Refresh" to check the status.
      </p>

      <div className="w-full space-y-3">
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="w-full py-4 rounded-full font-semibold text-sm text-white flex items-center justify-center gap-2"
          style={{ backgroundColor: C.accent }}
        >
          {isRefreshing && <Loader2 className="w-4 h-4 animate-spin" />}
          Refresh status
        </button>
        <button
          onClick={onRetry}
          disabled={isRetrying}
          className="w-full py-4 rounded-full font-semibold text-sm flex items-center justify-center gap-2"
          style={{ border: `1px solid ${C.border}`, color: C.muted }}
        >
          {isRetrying && <Loader2 className="w-4 h-4 animate-spin" />}
          Return to Stripe
        </button>
        <button
          onClick={() => navigate(-1)}
          className="w-full py-3 text-sm text-center"
          style={{ color: C.muted }}
        >
          Back to settings
        </button>
      </div>
    </div>
  );
}

// ─── Success (already onboarded) state ────────────────────────────────────────

function SuccessScreen() {
  const navigate = useNavigate();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen px-5" style={{ backgroundColor: C.bg }}>
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
        style={{ backgroundColor: '#22C55E22' }}
      >
        <CheckCircle className="w-7 h-7" style={{ color: C.green }} />
      </div>
      <h1 className="text-xl font-bold text-center mb-2" style={{ color: C.text }}>
        Wallet active
      </h1>
      <p className="text-sm text-center mb-10" style={{ color: C.muted }}>
        Your Stripe account is connected and you're ready to receive payments from roll review requests.
      </p>
      <div className="w-full space-y-3">
        <button
          onClick={() => navigate('/wallet')}
          className="w-full py-4 rounded-full font-semibold text-sm text-white"
          style={{ backgroundColor: C.accent }}
        >
          View my earnings
        </button>
        <button
          onClick={() => navigate(-1)}
          className="w-full py-3 text-sm text-center"
          style={{ color: C.muted }}
        >
          Back to settings
        </button>
      </div>
    </div>
  );
}

// ─── Unsupported country gate ─────────────────────────────────────────────────

/**
 * Shown only when the instructor's country is known and is not payable.
 *
 * It used to double as the screen for a NULL country, under a `reason:
 * 'missing'` branch reading "We couldn't determine your country" — the dead end
 * `PayoutCountryStep` now replaces, since the country is asked for in the flow
 * instead of demanded of it. So this screen answers one question only: your
 * country is X, and X cannot be paid yet.
 *
 * `onChangeCountry` reopens the picker rather than navigating to the profile
 * editor. Someone who selected the wrong country needs one tap to fix it, not a
 * trip out of the flow and back.
 */
function UnsupportedCountryScreen({ onChangeCountry }: { onChangeCountry: () => void }) {
  const navigate = useNavigate();
  const { t } = useI18n();

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: C.bg }}>
      <div className="flex justify-end px-5 pt-5">
        <button
          onClick={() => navigate(-1)}
          aria-label="Close"
          className="w-8 h-8 flex items-center justify-center rounded-full"
          style={{ backgroundColor: '#FFFFFF14' }}
        >
          <X className="w-4 h-4" style={{ color: C.muted }} />
        </button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-5 text-center">
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: `${C.accent}22` }}
        >
          <Globe className="w-7 h-7" style={{ color: C.accent }} />
        </div>
        <h1
          data-testid="unsupported-country-title"
          className="text-xl font-bold mb-3 max-w-[320px]"
          style={{ color: C.text }}
        >
          {t('earnings.unsupported_country_title')}
        </h1>
        <p
          data-testid="unsupported-country-body"
          className="text-sm leading-relaxed max-w-[320px]"
          style={{ color: C.muted }}
        >
          {t('earnings.unsupported_country_body')}
        </p>
      </div>

      <div className="px-5 pb-8 pt-4 space-y-3">
        <button
          data-testid="unsupported-country-contact"
          onClick={() => navigate('/contact')}
          className="w-full py-4 rounded-full font-semibold text-sm text-white"
          style={{ backgroundColor: C.accent }}
        >
          {t('earnings.unsupported_country_cta')}
        </button>
        <button
          data-testid="unsupported-country-set-country"
          onClick={onChangeCountry}
          className="w-full py-4 rounded-full font-semibold text-sm"
          style={{ border: `1px solid ${C.border}`, color: C.text }}
        >
          {t('earnings.unsupported_country_set_country')}
        </button>
      </div>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export function EarningsPage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast }      = useToast();
  const { t }          = useI18n();

  const [screen, setScreen] = useState<Screen>('welcome');
  const [isSavingService, setIsSavingService] = useState(false);
  const [isPickingCountry, setIsPickingCountry] = useState(false);

  const {
    account,
    isLoading,
    isOnboarded,
    startOnboarding,
    isStartingOnboarding,
    refreshStatus,
    isRefreshingStatus,
  } = useStripeConnect();

  const { profile, isLoading: profileLoading } = useUserProfile();

  const { updateConfig } = useAgendaConfig();
  const { setAcceptRollReviews } = useRollReviewSettings();

  // ── Redirect back from Stripe ──────────────────────────────────────────────
  //
  // `status` is read and then never used, and nothing anywhere sets `screen` to
  // 'service': `StripeScreen`'s continue handler redirects to Stripe, and the
  // return lands on a fresh mount where the `isOnboarded` / `account` branches
  // below answer first. So the service step — price and accept-requests — is
  // unreachable in this flow, and an instructor's Roll Review price is never
  // captured during onboarding.
  //
  // Left as-is deliberately: making it reachable means reordering those two
  // branches, which is what currently guarantees an onboarded or
  // pending-verification instructor is never shown the wizard again. That is a
  // product decision, not a copy fix, and belongs to its own change.
  const returnedStatus = searchParams.get('status');

  const showGenericError = () =>
    toast({
      title: 'Error',
      description: t('earnings.onboarding_generic_error'),
      variant: 'destructive',
    });

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: C.bg }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.accent }} />
      </div>
    );
  }

  // ── Already onboarded ──────────────────────────────────────────────────────
  if (isOnboarded) {
    return <SuccessScreen />;
  }

  // ── Account exists but Stripe not yet confirmed ────────────────────────────
  // (happens when: user completed wizard, went to Stripe, came back, but
  //  charges_enabled is still false — never show the wizard again in this case)
  if (account && !isOnboarded) {
    const handleRetryStripe = async () => {
      try {
        const result = await startOnboarding();
        if (result.onboarding_url) window.location.href = result.onboarding_url;
      } catch {
        showGenericError();
      }
    };

    const handleRefresh = async () => {
      try {
        await refreshStatus();
        toast({ title: 'Status updated', description: 'Check if your account is now active.' });
      } catch {
        showGenericError();
      }
    };

    return (
      <PendingScreen
        onRetry={handleRetryStripe}
        onRefresh={handleRefresh}
        isRetrying={isStartingOnboarding}
        isRefreshing={isRefreshingStatus}
      />
    );
  }

  // ── Pre-flight country gate ────────────────────────────────────────────────
  //
  // Only runs when no Stripe account exists yet (first-time setup). If the
  // instructor already started onboarding (account exists), the branches above
  // handle the appropriate state — never re-show this screen mid-flow.
  //
  // Payouts remain United States only; `isPayoutCountrySupported` is untouched.
  // What changed is what happens when the answer is not yet known. Onboarding
  // never collects a country, so `profiles.country` is NULL for most
  // instructors, and this gate used to end their setup on a screen telling them
  // to go and fill the field somewhere else. Now the question is asked here, and
  // the gate re-evaluates against the saved answer on the next render.
  //
  // `isPickingCountry` is the manual re-entry: an instructor who is blocked
  // because they picked the wrong country can reopen the picker from the
  // unsupported screen.
  const country = (profile as { country?: string | null } | null)?.country;

  if (!isPayoutCountrySupported(country)) {
    if (!country || isPickingCountry) {
      return (
        <PayoutCountryStep
          initialCountry={country}
          // Nothing here forces a re-render, and nothing needs to: the step
          // invalidates the `user-profile` query before calling back, so
          // `profile.country` is already the saved value on the next render and
          // this gate re-evaluates on its own. This only closes the manual
          // re-entry below.
          onSaved={() => setIsPickingCountry(false)}
          onDismiss={() => navigate(-1)}
        />
      );
    }

    return <UnsupportedCountryScreen onChangeCountry={() => setIsPickingCountry(true)} />;
  }

  // ── Wizard ─────────────────────────────────────────────────────────────────

  if (screen === 'welcome') {
    return <WelcomeScreen onStart={() => setScreen('contract')} />;
  }

  if (screen === 'contract') {
    return (
      <ContractScreen
        onBack={() => setScreen('welcome')}
        onContinue={() => setScreen('stripe')}
      />
    );
  }

  if (screen === 'stripe') {
    const handleStripe = async () => {
      try {
        const result = await startOnboarding();
        if (result.onboarding_url) window.location.href = result.onboarding_url;
      } catch {
        showGenericError();
      }
    };

    return (
      <StripeScreen
        onBack={() => setScreen('contract')}
        onContinue={handleStripe}
        isLoading={isStartingOnboarding}
      />
    );
  }

  // screen === 'service'
  const handleFinish = async (price: number, acceptRequests: boolean) => {
    setIsSavingService(true);
    try {
      await Promise.all([
        updateConfig({ class_value: price, currency: CURRENCY }),
        new Promise<void>((resolve, reject) => {
          try { setAcceptRollReviews(acceptRequests); resolve(); }
          catch (e) { reject(e); }
        }),
      ]);
      navigate('/wallet');
    } catch {
      showGenericError();
    } finally {
      setIsSavingService(false);
    }
  };

  return (
    <ServiceScreen
      onBack={() => setScreen('stripe')}
      onFinish={handleFinish}
      isSaving={isSavingService}
    />
  );
}

export default EarningsPage;
